/**
 * Advanced Search Component for v0.7.0
 * Multi-criteria search panel with price range slider
 * [UPDATED v0.7.2] Added PriceRangeSlider component
 */

import { useState } from 'react';
import type { AdvancedSearchParams } from '../../types';
import { CATEGORIES, REGIONS } from '../../types';
import { PRICE_PRESETS, saveSearchHistory, getSearchHistory, removeSearchHistory } from '../../services/searchApi';
import PriceRangeSlider from './PriceRangeSlider';
import IconSearch from '../icons/IconSearch';
import IconArrowPath from '../icons/IconArrowPath';
import IconBookmark from '../icons/IconBookmark';
import './AdvancedSearch.css';

interface AdvancedSearchProps {
  className?: string;
  onSearch: (params: AdvancedSearchParams) => void;
  defaultParams?: Partial<AdvancedSearchParams>;
}

const AdvancedSearch = ({ className = '', onSearch, defaultParams = {} }: AdvancedSearchProps) => {
  const [params, setParams] = useState<AdvancedSearchParams>({
    sortBy: 'date',
    sortOrder: 'desc',
    ...defaultParams,
  });
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState(getSearchHistory());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [useCustomPriceRange, setUseCustomPriceRange] = useState(false);

  const updateParam = <K extends keyof AdvancedSearchParams>(
    key: K,
    value: AdvancedSearchParams[K]
  ) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    onSearch(params);
  };

  const handleReset = () => {
    const resetParams: AdvancedSearchParams = {
      sortBy: 'date',
      sortOrder: 'desc',
    };
    setParams(resetParams);
    onSearch(resetParams);
  };

  const handleSaveSearch = () => {
    if (!saveName.trim()) return;
    saveSearchHistory(saveName, params);
    setSearchHistory(getSearchHistory());
    setSaveDialogOpen(false);
    setSaveName('');
    setShowHistory(true);
  };

  const handleLoadHistory = (historyParams: AdvancedSearchParams) => {
    setParams(historyParams);
    onSearch(historyParams);
    setShowHistory(false);
  };

  const handleRemoveHistory = (id: string) => {
    removeSearchHistory(id);
    setSearchHistory(getSearchHistory());
  };

  const priceRange = params.priceRange || { min: null, max: null };
  const priceMatch = PRICE_PRESETS.find(
    (p) => p.min === priceRange.min && p.max === priceRange.max
  );

  return (
    <div className={`advanced-search ${className}`}>
      <div className="advanced-search__header">
        <h3 className="advanced-search__title">進階搜尋</h3>
        <button
          className="advanced-search__toggle"
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? '隱藏' : '搜尋歷史'}
        </button>
      </div>

      {/* Search History */}
      {showHistory && (
        <div className="advanced-search__history">
          <div className="advanced-search__history-header">
            <span>搜尋歷史</span>
            <button
              className="btn--link"
              onClick={() => {
                if (confirm('確定要清空搜尋歷史嗎？')) {
                  localStorage.removeItem('search_history');
                  setSearchHistory([]);
                }
              }}
            >
              清空
            </button>
          </div>
          {searchHistory.length === 0 ? (
            <p className="advanced-search__history-empty">沒有搜尋歷史</p>
          ) : (
            <ul className="advanced-search__history-list">
              {searchHistory.map((item) => (
                <li key={item.id} className="history-item">
                  <button
                    className="history-item__button"
                    onClick={() => handleLoadHistory(item.params)}
                  >
                    {item.name}
                  </button>
                  <button
                    className="history-item__remove"
                    onClick={() => handleRemoveHistory(item.id)}
                    title="刪除"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Search Form */}
      <div className="advanced-search__scrollable">
        <div className="advanced-search__form">
        {/* Sort Options - MOVED TO TOP */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">排序依據</label>
            <select
              value={params.sortBy || 'date'}
              onChange={(e) => updateParam('sortBy', e.target.value as 'date' | 'price' | 'popularity')}
              className="form-select"
            >
              <option value="date">發布時間</option>
              <option value="price">價格</option>
              <option value="popularity">熱門度</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">排序順序</label>
            <select
              value={params.sortOrder || 'desc'}
              onChange={(e) => updateParam('sortOrder', e.target.value as 'asc' | 'desc')}
              className="form-select"
            >
              <option value="desc">由新到舊</option>
              <option value="asc">由舊到新</option>
            </select>
          </div>
        </div>

        {/* Keyword - MOVED UP */}
        <div className="form-group form-group--full">
          <label className="form-label">關鍵字</label>
          <input
            type="text"
            placeholder="搜尋廣告標題、描述或店名..."
            value={params.keyword || ''}
            onChange={(e) => updateParam('keyword', e.target.value || undefined)}
            className="form-input"
          />
        </div>

        {/* Has Image Filter - MOVED UP */}
        <div className="form-group">
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={params.hasImage || false}
              onChange={(e) => updateParam('hasImage', e.target.checked || undefined)}
            />
            <span>只顯示有圖片的廣告</span>
          </label>
        </div>

        {/* Category - STAYS SAME POSITION */}
        <div className="form-group">
          <label className="form-label">類別（可多選）</label>
          <div className="checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={!params.category && (!params.categories || params.categories.length === 0)}
                onChange={() => {
                  updateParam('category', undefined);
                  updateParam('categories', []);
                }}
              />
              <span>所有類別</span>
            </label>
            {CATEGORIES.map((cat) => (
              <label key={cat} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={params.categories?.includes(cat) || false}
                  onChange={(e) => {
                    const current = params.categories || [];
                    if (e.target.checked) {
                      updateParam('categories', [...current, cat]);
                      updateParam('category', undefined);
                    } else {
                      updateParam('categories', current.filter(c => c !== cat));
                    }
                  }}
                />
                <span>{cat}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Region - STAYS SAME POSITION */}
        <div className="form-group">
          <label className="form-label">地區（可多選）</label>
          <div className="checkbox-group checkbox-group--scroll">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={!params.region && (!params.regions || params.regions.length === 0)}
                onChange={() => {
                  updateParam('region', undefined);
                  updateParam('regions', []);
                }}
              />
              <span>所有地區</span>
            </label>
            {REGIONS.map((reg) => (
              <label key={reg} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={params.regions?.includes(reg) || false}
                  onChange={(e) => {
                    const current = params.regions || [];
                    if (e.target.checked) {
                      updateParam('regions', [...current, reg]);
                      updateParam('region', undefined);
                    } else {
                      updateParam('regions', current.filter(r => r !== reg));
                    }
                  }}
                />
                <span>{reg}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Range - STAYS SAME POSITION */}
        <div className="form-group">
          <label className="form-label">價格區間</label>

          {/* Toggle between preset and custom */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label className="checkbox-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useCustomPriceRange}
                onChange={(e) => setUseCustomPriceRange(e.target.checked)}
              />
              <span>自訂價格範圍</span>
            </label>
          </div>

          {!useCustomPriceRange ? (
            /* Preset dropdown */
            <select
              value={priceMatch ? PRICE_PRESETS.indexOf(priceMatch) : -1}
              onChange={(e) => {
                const preset = PRICE_PRESETS[parseInt(e.target.value)];
                updateParam('priceRange', { min: preset.min, max: preset.max });
              }}
              className="form-select"
            >
              {PRICE_PRESETS.map((preset, i) => (
                <option key={i} value={i}>{preset.label}</option>
              ))}
            </select>
          ) : (
            /* Custom range slider */
            <PriceRangeSlider
              value={params.priceRange || { min: null, max: null }}
              onChange={(newValue) => updateParam('priceRange', newValue)}
              min={0}
              max={20000}
              step={100}
            />
          )}
        </div>
        </div>
      </div>

      {/* Actions */}
      <div className="advanced-search__actions">
        <button
          onClick={() => setSaveDialogOpen(true)}
          className="btn btn--outline btn--with-icon"
        >
          <IconBookmark className="btn__icon" />
          <span>儲存搜尋</span>
        </button>
        <button onClick={handleReset} className="btn btn--outline btn--with-icon">
          <IconArrowPath className="btn__icon" />
          <span>重設</span>
        </button>
        <button onClick={handleSearch} className="btn btn--primary btn--with-icon">
          <IconSearch className="btn__icon" />
          <span>搜尋</span>
        </button>
      </div>

      {/* Save Search Dialog */}
      {saveDialogOpen && (
        <div className="advanced-search__dialog-overlay" onClick={() => setSaveDialogOpen(false)}>
          <div className="advanced-search__dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="dialog__title">儲存搜尋條件</h3>
            <input
              type="text"
              placeholder="輸入名稱（例如：台北地區維修）"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="form-input"
              autoFocus
            />
            <div className="dialog__actions">
              <button
                onClick={handleSaveSearch}
                className="btn btn--primary"
                disabled={!saveName.trim()}
              >
                儲存
              </button>
              <button
                onClick={() => setSaveDialogOpen(false)}
                className="btn btn--outline"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
